import { useEffect, useRef, useState } from "react";
import { useBarragemContext } from "../../context";
import { RequestError, useApi } from "../../services";
import { Controle } from "../../services/api/controle";
import { ErrorOnDialog, LoadingModal } from "../modals";
import { Box, Grid2, IconButton, Paper, TextField, Tooltip, Typography } from "@mui/material";
import { HidroDXEditor } from "../edit";
import { MDXEditorMethods } from "@mdxeditor/editor";
import { Add, Delete } from "@mui/icons-material";
import { generateRandomHash } from "../../core";
import { PhonesEditor } from "../phonesEditor";

export const CentroControle: React.FC = () => {
  const api = useApi();
  const { barragem, setBarragem } = useBarragemContext();

  const descricaoRef = useRef<MDXEditorMethods>(null);
  const localizacaoRef = useRef<MDXEditorMethods>(null);
  const instrumentosRef = useRef<MDXEditorMethods>(null);

  const [centro, setCentro] = useState<Controle>();
  const [error, setError] = useState<RequestError>();

  useEffect(() => {
    if (!barragem || "error" in barragem) return;

    if (barragem.salaControle === null) {
      setCentro({
        id: -1,
        nome: "",
        descricao: "",
        localizacao: "",
        supervisorios: "",
        telefones: "",
        funcionamento: 24,
        freqInspecao: 15,
        instrumentos: "",
        dataCadastro: new Date(),
      });
      return;
    }

    if ("error" in barragem.salaControle) {
      setError(barragem.salaControle);
      return;
    }

    setCentro(barragem.salaControle);
  }, [barragem]);

  useEffect(() => {
    if (!centro) return;

    if (descricaoRef) descricaoRef.current?.setMarkdown(centro.descricao ?? "");
    if (localizacaoRef) localizacaoRef.current?.setMarkdown(centro.localizacao ?? "");
    if (instrumentosRef) instrumentosRef.current?.setMarkdown(centro.instrumentos ?? "");

  }, [centro, localizacaoRef, instrumentosRef, descricaoRef]);

  const handleChange = (newCentro: Controle) => {
    if (!barragem || "error" in barragem || !centro) return;
    setBarragem({
      ...barragem,
      salaControle: newCentro,
    });

    api.controle.update(barragem.id, newCentro).then((value) => {
      if ("error" in value) {
        setError(value);
        setBarragem({
          ...barragem,
          salaControle: centro,
        });
        return;
      }
      setBarragem({
        ...barragem,
        salaControle: value,
      });
    });
  };



  if (!centro) return <LoadingModal open={true} />;

  return <>
    <ErrorOnDialog
      open={Boolean(error)}
      error={error?.error ?? ""}
      message={error?.message ?? ""}
      onClose={() => setError(undefined)}
    />

    <Box sx={{
      p: "1em",
      gap: "1em",
      flex: 1,
      height: "100%",
      display: "flex",
      flexDirection: "row",
    }}>
      <Box sx={{
        flex: 1,
        gap: "1em",
        display: "flex",
        flexDirection: "column",
      }}>
        <Grid2 container spacing={"1em"}>
          <Grid2 size={12}>
            <TextField fullWidth label="Nome" variant="standard"
              value={centro.nome}
              onChange={(ev) => handleChange({
                ...centro,
                nome: ev.target.value,
              })}
            />
          </Grid2>

          <Grid2 size={6}>
            <TextField fullWidth label="Funcionamento (h/dia)" variant="standard"
              value={centro.funcionamento}
              type="number"
              onChange={(ev) => {
                let value = parseInt(ev.target.value.replaceAll(" ", "").replaceAll(",", "."));
                if (isNaN(value)) return;

                if (value > 24) value = 24;
                if (value < 0) value = 0;

                handleChange({
                  ...centro,
                  funcionamento: value,
                });
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <TextField fullWidth label="Frequência de Inspeção (dias)" variant="standard"
              value={centro.freqInspecao}
              type="number"
              onChange={(ev) => {
                let value = parseInt(ev.target.value.replaceAll(" ", "").replaceAll(",", "."));
                if (isNaN(value)) return;

                if (value < 0) value = 0;

                handleChange({
                  ...centro,
                  freqInspecao: value,
                });
              }}
            />
          </Grid2>

        </Grid2>

        {/* Título supevisório e telefones */}
        <Box sx={{
          gap: "1em",
          width: "100%",
          display: "flex",
          overflow: "auto",
          flexDirection: "row",
        }}>
          <div style={{ flex: 1, display: "flex", flexDirection: "row" }}>
            <Typography>Supervisórios:</Typography>
            <div style={{ flex: 1 }} />
            <Tooltip title="Adicionar novo supervisório">
              <IconButton size="small" onClick={() => handleChange({
                ...centro,
                supervisorios: (centro.supervisorios ?? "") + ";",
              })}>
                <Add fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>

          <div style={{ flex: 1, display: "flex", flexDirection: "row" }}>
            <Typography>Telefones:</Typography>
            <div style={{ flex: 1 }} />
            <Tooltip title="Adicionar novo telefone">
              <IconButton size="small" onClick={() => handleChange({
                ...centro,
                telefones: (centro.telefones ?? "") + ";",
              })}>
                <Add fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </Box>

        <Box sx={{
          flex: 1,
          display: "flex",
          overflow: "auto",
          flexDirection: "row",
        }}>

          {/* Supervisórios */}
          <Box
            key={generateRandomHash(5)}
            sx={{
              flex: 1,
              gap: "0.5em",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {(centro.supervisorios ?? "").split(";").map((sup, idx) => {
              return <Item
                key={idx}
                value={sup}
                onChange={(value) => {
                  const values = (centro.supervisorios??"").split(";");
                  values[idx] = value;
                  handleChange({
                    ...centro,
                    supervisorios: values.join(";"),
                  });
                }}
                onRemove={() => {
                  const values = (centro.supervisorios ?? "").split(";").filter((_, i) => i !== idx);
                  handleChange({
                    ...centro,
                    supervisorios: values.join(";"),
                  });
                }}
              />;
            })}
          </Box>

          {/* Telefones */}
          <PhonesEditor
            key={generateRandomHash(5)}
            phones={(centro.telefones ?? "")}
            onEdit={(phones) => handleChange({
              ...centro,
              telefones: phones,
            })}
          />
        </Box>
      </Box>

      {/* Descrição */}
      <Paper elevation={0} sx={{
        flex: 1,
        p: "1em",
        width: "33%",
        height: "100%",
      }}>
        <Typography fontSize={22}>Descrição:</Typography>
        <Box sx={{
          height: "95%",
          display: "flex",
          overflow: "auto",
          flexDirection: "column",
        }}>
          <HidroDXEditor
            editorRef={descricaoRef}
            image={false}
            chartMap={false}
            onChange={(value) => handleChange({
              ...centro,
              descricao: value,
            })}
          />
        </Box>
      </Paper>

      {/* Editores de texto */}
      <Box sx={{
        flex: 1,
        gap: "1em",
        width: "33%",
        display: "flex",
        flexDirection: "column",
      }}>
        {/* Localização */}
        <Paper elevation={0} sx={{ flex: 1, p: "1em", height: "45%" }}>
          <Typography fontSize={22}>Localização:</Typography>
          <Box sx={{
            height: "95%",
            display: "flex",
            overflow: "auto",
            flexDirection: "column",
          }}>
            <HidroDXEditor
              editorRef={localizacaoRef}
              image={false}
              chartMap={false}
              onChange={(value) => handleChange({
                ...centro,
                localizacao: value,
              })}
            />
          </Box>
        </Paper>

        {/* Instrumentos */}
        <Paper elevation={0} sx={{ flex: 1, p: "1em", height: "45%" }}>
          <Typography fontSize={22}>Instrumentos:</Typography>
          <Box sx={{
            height: "95%",
            display: "flex",
            overflow: "auto",
            flexDirection: "column",
          }}>
            <HidroDXEditor
              editorRef={instrumentosRef}
              image={false}
              chartMap={false}
              onChange={(value) => handleChange({
                ...centro,
                instrumentos: value,
              })}
            />
          </Box>
        </Paper>
      </Box >
    </Box >
  </>;
};

interface ItemProps {
  label?: string;
  value: string;
  onChange?: (value: string) => void;
  onRemove?: () => void;
}
const Item: React.FC<ItemProps> = ({ value, label, onChange, onRemove }) => {
  const timer = useRef<NodeJS.Timer>();
  const [textValue, setTextValue] = useState(value);

  const handleChange = (value: string) => {
    if (timer.current) clearTimeout(timer.current);
    setTextValue(value);

    timer.current = setTimeout(() => {
      if (onChange) onChange(value);
    }, 1000);
  };

  return <Box sx={{
    display: "flex",
    flexDirection: "row",
  }}>
    <TextField
      fullWidth
      label={label}
      variant="standard"
      value={textValue}
      onChange={(ev) => handleChange(ev.target.value)}
    />
    <Tooltip title="Remover">
      <IconButton size="small" onClick={onRemove}>
        <Delete fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>;
};